import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { Heading1, Stack } from '@myphx-engineering/phx-design-system';
import { constants } from '@myphx-engineering/phx-design-system/framework';

import {
    useDrugProgram,
    useMedicationConfigOverride,
} from '../../../contexts/DrugProgramProvider';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { usePrescriberOrder } from '../../../hooks/use-prescriber-order';
import { getNdcDefinition } from '../../../util/config/get-ndc-definition';
import { getPricingByNdc } from '../../../util/config/get-pricing';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { SwitchPrescriptionHeader } from '../../common/SwitchPrescriptionHeader';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import { KeepCurrentPrescriptionButton } from './KeepCurrentPrescriptionButton';
import { RequestSwitchButton } from './RequestSwitchButton';
import { SwitchToCashDetails } from './SwitchToCashDetails';
import { SwitchToInsuranceDetails } from './SwitchToInsuranceDetails';

const SwitchPrescriptionPage = () => {
    const { patient } = usePatientContext();
    const { t } = useTranslation();
    const drugProgram = useDrugProgram();
    const configOverride = useMedicationConfigOverride();

    const { order, orderId, medication } = usePrescriberOrder();

    const ndcDefinition = getNdcDefinition(drugProgram, medication);

    if (!ndcDefinition) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    // It is known that there will only be a single ndc for the LillyDirect app, but the schema supports multiple
    const switchedMedication = ndcDefinition.alternateConfigs[0];
    const switchDrugPricing = getPricingByNdc(
        configOverride,
        switchedMedication.alternateNdc
    );

    if (order.__typename !== 'DigitallyReceivedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.verifyPatient', {
                    prescriberOrderId: order.drxId ?? '',
                })}
            />
        );
    }

    if (!switchDrugPricing) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const heading =
        switchDrugPricing.type === 'insurance'
            ? t('switchPage.toInsuranceHeading', {
                  reg: constants.unicode.REGISTERED,
              })
            : t('switchPage.toCashHeading', {
                  reg: constants.unicode.REGISTERED,
              });

    return (
        <Stack gap="lg">
            <Heading1>{heading}</Heading1>
            <SwitchPrescriptionHeader
                switchedMedicationId={switchedMedication.alternateNdc}
                originalQuantity={order.quantity}
            />
            <Stack gap="xxl">
                <ConditionalComponent
                    condition={switchDrugPricing.type === 'selfPay'}
                >
                    <SwitchToCashDetails pricingInfo={switchDrugPricing} />
                </ConditionalComponent>

                <ConditionalComponent
                    condition={switchDrugPricing.type === 'insurance'}
                >
                    <SwitchToInsuranceDetails pricingInfo={switchDrugPricing} />
                </ConditionalComponent>
            </Stack>

            <RequestSwitchButton orderId={orderId} patient={patient} />
            <KeepCurrentPrescriptionButton prescriberOrderId={orderId} />
        </Stack>
    );
};

export default SwitchPrescriptionPage;
